
import { useState } from 'react';
import type { PrayerImage } from '../../../../lib/supabase';

interface ImageListProps {
  images: PrayerImage[];
  onDelete: (id: string) => void;
  onDeleteWeek: (weekDate: string) => void;
}

export default function ImageList({ images, onDelete, onDeleteWeek }: ImageListProps) {
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);

  if (images.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-image-line text-3xl text-gray-400"></i>
        </div>
        <p className="text-gray-500">등록된 기도제목 이미지가 없습니다.</p>
        <p className="text-sm text-gray-400 mt-2">
          왼쪽에서 이미지를 업로드해주세요.
        </p>
      </div>
    );
  }

  // 주간별로 그룹화
  const groupedByWeek = images.reduce((acc, image) => {
    const week = image.week_date;
    if (!acc[week]) {
      acc[week] = [];
    }
    acc[week].push(image);
    return acc;
  }, {} as Record<string, PrayerImage[]>);

  // 날짜 내림차순 정렬
  const sortedWeeks = Object.keys(groupedByWeek).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
      {sortedWeeks.map((week) => {
        const weekImages = groupedByWeek[week].sort((a, b) => a.display_order - b.display_order);
        const isExpanded = expandedWeek === week;

        return (
          <div
            key={week}
            className="border border-gray-200 rounded-xl overflow-hidden bg-white"
          >
            {/* 주간 헤더 */}
            <div
              onClick={() => setExpandedWeek(isExpanded ? null : week)}
              className="flex items-center justify-between px-5 py-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <i className="ri-calendar-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {new Date(week).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </h3>
                  <p className="text-sm text-gray-500">{weekImages.length}장의 이미지</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteWeek(week);
                  }}
                  className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  전체 삭제
                </button>
                <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-xl text-gray-400`}></i>
              </div>
            </div>

            {/* 이미지 그리드 */}
            {isExpanded && (
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {weekImages.map((image, index) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.image_url}
                        alt={`기도제목 ${index + 1}`}
                        className="w-full h-28 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        {index + 1}
                      </div>
                      <button
                        onClick={() => onDelete(image.id)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
